/** MUI theme overrides for the design-system-components (npm) controls, which paint MUI's palette
  defaults. The vars are set only under .point-ds, so legacy falls back to MUI's own default literals. */
const POPUP_PAPER = {
  backgroundColor: 'var(--legacy-popup-bg, #fff)',
  color: 'var(--legacy-popup-text-color, rgba(0, 0, 0, 0.87))'
};
// Calendar popup: Point dark flips it to a dark card while other popups stay on the light overlay surface.
const CALENDAR_PAPER = {
  backgroundColor: 'var(--calendar-popup-bg, var(--legacy-popup-bg, #fff))',
  color: 'var(--calendar-popup-text-color, var(--legacy-popup-text-color, rgba(0, 0, 0, 0.87)))'
};
const INPUT_ICON_COLOR = 'var(--legacy-input-icon-color, rgba(0, 0, 0, 0.54))';

export const LegacyMuiOverrides = {
  MuiPopover: { styleOverrides: { paper: POPUP_PAPER } },
  MuiSelect: { styleOverrides: { icon: { color: INPUT_ICON_COLOR } } },
  MuiSlider: { styleOverrides: { markLabel: { color: 'var(--legacy-slider-mark-label-color, rgba(0, 0, 0, 0.6))' } } },
  MuiAutocomplete: {
    styleOverrides: {
      popupIndicator: { color: INPUT_ICON_COLOR },
      clearIndicator: { color: INPUT_ICON_COLOR },
      paper: POPUP_PAPER,
      noOptions: { color: 'var(--legacy-popup-secondary-text-color, rgba(0, 0, 0, 0.6))' },
      loading: { color: 'var(--legacy-popup-secondary-text-color, rgba(0, 0, 0, 0.6))' },
      listbox: {
        '& .MuiAutocomplete-option.Mui-focused': {
          backgroundColor: 'var(--legacy-list-option-hover-bg, rgba(0, 0, 0, 0.04))',
          '@media (hover: none)': { backgroundColor: 'transparent' }
        }
      }
    }
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        '&:hover': {
          backgroundColor: 'var(--legacy-list-option-hover-bg, rgba(0, 0, 0, 0.04))',
          '@media (hover: none)': { backgroundColor: 'transparent' }
        },
        '&.Mui-focusVisible': { backgroundColor: 'var(--legacy-list-option-hover-bg, rgba(0, 0, 0, 0.12))' }
      }
    }
  },
  MuiInputBase: {
    styleOverrides: {
      root: { color: 'var(--legacy-input-text-color, rgba(0, 0, 0, 0.87))' },
      // No !important, so MUI's opacity 0 still hides the placeholder behind an unshrunk label.
      input: {
        '&::placeholder': {
          color: 'var(--legacy-input-placeholder-color, currentColor)',
          opacity: 'var(--legacy-input-placeholder-opacity, 0.42)'
        }
      }
    }
  },
  MuiIconButton: {
    styleOverrides: {
      root: ({ theme, ownerState }) => ({
        ...(ownerState.color === 'default' && {
          color: `var(--legacy-icon-button-color, ${theme.palette.action.active})`
        }),
        '&.Mui-disabled': { color: `var(--legacy-icon-button-color-disabled, ${theme.palette.action.disabled})` }
      })
    }
  },
  // The npm CustomModal / ExtendedModal paint their title, message and backdrop from class rules with no theme
  // slot, so those need the gated descendant. The DefaultTitle/Message classes are the package's displayNames.
  // Gated because --mainModal* also carry legacy .dark-mode / .light-mode values.
  MuiDialog: {
    styleOverrides: {
      root: {
        '.point-ds &': {
          '& .MuiDialog-paper': {
            backgroundColor: 'var(--mainModalBgColor)',
            color: 'var(--mainModalContentFontColor)'
          },
          '& .MuiDialogTitle-root, & .MuiDialogTitle-root .text, & [class*="styled__DefaultTitle"]': {
            color: 'var(--mainModalTitleColor)'
          },
          '& [class*="styled__DefaultMessage"]': { color: 'var(--mainModalContentFontColor)' },
          '& .MuiBackdrop-root': { backgroundColor: 'var(--modal-backdrop-bg)' }
        }
      }
    }
  },
  MuiTableCell: { styleOverrides: { root: { fontFamily: 'var(--app-font), Averta, sans-serif' } } },
  MuiOutlinedInput: {
    styleOverrides: {
      // Overrides land after MUI's state rules, so focused/error/disabled are restated after hover in MUI's order.
      root: ({ theme, ownerState }) => ({
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: `var(--legacy-input-border-color-hover, ${theme.palette.text.primary})`
        },
        '@media (hover: none)': {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--legacy-input-border-color, rgba(0, 0, 0, 0.23))'
          }
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: `var(--legacy-input-border-color-focused, ${theme.palette[ownerState.color || 'primary'].main})`
        },
        '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.error.main },
        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
          borderColor: `var(--legacy-input-border-color-disabled, ${theme.palette.action.disabled})`
        },
        '&.Mui-disabled .MuiOutlinedInput-input': {
          WebkitTextFillColor: `var(--legacy-input-text-color-disabled, ${theme.palette.text.disabled})`
        }
      }),
      notchedOutline: { borderColor: 'var(--legacy-input-border-color, rgba(0, 0, 0, 0.23))' }
    }
  },
  MuiInputLabel: {
    styleOverrides: {
      // Restates FormLabel's state rules after the focused override, in MUI's order.
      root: ({ theme, ownerState }) => ({
        color: 'var(--legacy-input-label-color, rgba(0, 0, 0, 0.6))',
        '&.Mui-focused': {
          color: `var(--legacy-input-label-color-focused, ${theme.palette[ownerState.color || 'primary'].main})`
        },
        '&.Mui-disabled': { color: theme.palette.text.disabled },
        '&.Mui-error': { color: theme.palette.error.main }
      })
    }
  },
  MuiFormControlLabel: {
    styleOverrides: {
      label: ({ theme }) => ({
        '&.Mui-disabled': { color: `var(--legacy-input-text-color-disabled, ${theme.palette.text.disabled})` }
      })
    }
  },
  MuiPickersDay: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...CALENDAR_PAPER,
        '&.Mui-disabled': { color: `var(--legacy-icon-button-color-disabled, ${theme.palette.text.disabled})` },
        '&.MuiPickersDay-today:not(.Mui-selected)': {
          borderColor:
            'var(--calendar-popup-secondary-text-color, var(--legacy-popup-secondary-text-color, rgba(0, 0, 0, 0.6)))'
        }
      })
    }
  },
  // The picker popper's paper, weekday labels, arrows and the lab clock face/numbers are unnamed styled() parts
  // with no theme slot. They need a descendant selector, so it is gated to .point-ds and to picker content, and
  // legacy never matches it.
  MuiPopper: {
    styleOverrides: {
      root: {
        '.point-ds &:has(.MuiPickersDay-root, [class*="PickersYear"], [class*="PickersMonth"], [class*="MuiClockPicker"]) > .MuiPaper-root':
          {
            ...CALENDAR_PAPER,
            '& .MuiTypography-caption': {
              color: 'var(--calendar-popup-secondary-text-color, var(--legacy-popup-secondary-text-color))'
            },
            '& .MuiIconButton-root': { color: 'var(--calendar-popup-icon-color, var(--legacy-popup-icon-color))' },
            '& .MuiTab-root:not(.Mui-selected)': {
              color: 'var(--calendar-popup-icon-color, var(--legacy-popup-icon-color))'
            },
            '& div:has(> [role="listbox"])': { backgroundColor: 'var(--legacy-clock-face-bg)' },
            '& [role="option"]:not(.PrivateClockNumber-selected):not(.PrivateClockNumber-disabled)': {
              color: 'var(--calendar-popup-text-color, var(--legacy-popup-text-color))'
            }
          }
      }
    }
  },
  // The npm CustomTooltip paints var(--white) from a two-class popper rule, which a tooltip slot override can't beat.
  // Point has one tooltip style for every mode, so the gated descendant rule also matches the DSC CustomTooltip.
  MuiTooltip: {
    styleOverrides: {
      popper: {
        '.point-ds &': {
          '& .MuiTooltip-tooltip': { backgroundColor: 'var(--tooltip-bg-color)', color: 'var(--tooltip-color)' },
          '& .MuiTooltip-arrow': { color: 'var(--tooltip-bg-color)' }
        }
      }
    }
  }
};
